import { useEffect, useMemo } from 'react'
import { get } from 'lodash-es'

import { AssetTag } from '@/feature/tags/components/TableHeaderTags'
import { useTagsStore } from '@/feature/tags'

import { AssetView, AssetViewProps } from '@/lib/features/Assets/components/AssetView/AssetView'
import { useSingleAsset, useSinglePtu, useSingleVehicle } from '@/lib/features/Assets/api'
import {
  Asset,
  AssetContentType,
  AssetId,
  BridgeId,
  ObjectId,
} from '@/lib/features/Assets/interfaces'

export type ExpectedAssetViewProps = {
  expectedAsset: ExpectedAsset | null
  onDelete: AssetViewProps['onDelete']
  onShare: AssetViewProps['onShare']
  onClose?: AssetViewProps['onClose']
}

export type ExpectedAsset = {
  assetId: AssetId
  bridgeId: BridgeId
  contentType: AssetContentType
  description?: Asset['description']
  objectId?: ObjectId
  vehicleBridgeId?: string
  tags?: AssetTag[]
}

export const ExpectedAssetView = ({
  expectedAsset,
  onDelete,
  onShare,
  onClose = () => null,
}: ExpectedAssetViewProps) => {
  const vehicle = useSingleVehicle(
    expectedAsset?.contentType === AssetContentType.VEHICLE ? { id: expectedAsset.assetId } : null,
  )
  const ptu = useSinglePtu(
    expectedAsset?.contentType === AssetContentType.PTU ? { id: expectedAsset.assetId } : null,
  )
  const { data: asset, mutate: refetchAsset } = useSingleAsset({ id: expectedAsset?.assetId })
  const isLoading = !expectedAsset || vehicle.isLoading || ptu.isLoading

  const { activeAssetData } = useTagsStore()

  const assetData: Asset | null = useMemo(() => {
    if (!expectedAsset) {
      return null
    }

    const expectedAssetData =
      expectedAsset.contentType === AssetContentType.VEHICLE ? vehicle.data : ptu.data

    if (!expectedAssetData) {
      return null
    }

    return {
      ...expectedAssetData,
      contentType: expectedAsset.contentType,
      bridgeDateFrom: expectedAssetData.currentShares?.[0]?.dateFrom || null,
      bridgeDateTo: expectedAssetData.currentShares?.[0]?.dateTo || null,
      bridgeId:
        expectedAssetData.currentShares?.[0]?.bridgeId || expectedAssetData.bridgeId || null,
      bridgeStatus: expectedAssetData.currentShares?.[0]?.bridgeStatus || null,
      mapIsActive: expectedAssetData.currentShares?.[0]?.isActive,
      reference: expectedAssetData.currentShares?.[0]?.reference,
      vehicleBridgeId:
        expectedAsset.vehicleBridgeId || get(expectedAssetData, 'vehicleBridgeId', null),
      tags: asset?.tags,
    } as Asset
  }, [expectedAsset, ptu, vehicle, asset])

  useEffect(() => {
    refetchAsset()
  }, [activeAssetData])

  if (!expectedAsset) {
    return null
  }

  return (
    <AssetView
      asset={assetData}
      expectedAsset={expectedAsset}
      isLoading={isLoading}
      onClose={onClose}
      onDelete={onDelete}
      onShare={onShare}
    />
  )
}
